import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { stopNativeDriverTracking, supportsNativeBackgroundLocation } from '@/lib/nativeBackgroundLocation';
import { stopNativePush } from '@/lib/nativePush';
import { createSessionCoordinator } from '@/lib/session-coordinator.mjs';

const AuthContext = createContext(null);
export function AuthProvider({ children }) {
  const [state, setState] = useState({ user: null, loading: true, warning: null });
  const [retrying, setRetrying] = useState(false);
  const coordinator = useRef(null);
  if (!coordinator.current) coordinator.current = createSessionCoordinator({
    api,
    storage: { getItem: (...args) => localStorage.getItem(...args), setItem: (...args) => localStorage.setItem(...args), removeItem: (...args) => localStorage.removeItem(...args) },
    changed: () => window.dispatchEvent(new Event('ca-credential-changed')),
    stopTracking: () => supportsNativeBackgroundLocation() ? stopNativeDriverTracking() : Promise.resolve(),
    // Push support is determined by its own adapter; it does not depend on GPS support.
    stopPush: stopNativePush,
    publish: patch => setState(previous => ({ ...previous, ...patch })),
  });
  const refresh = useCallback(() => coordinator.current.refresh(), []);
  const login = useCallback((email, password) => coordinator.current.login('/auth/login', { email, password }), []);
  const loginWithGoogle = useCallback((credential, portal = 'corporate') => coordinator.current.login('/auth/google', { credential, portal }), []);
  const registerCustomer = useCallback(({ name, email, phone, password }) => coordinator.current.login('/auth/customer/register', { name, email, phone, password }), []);
  const activateAccount = useCallback(payload => coordinator.current.login('/auth/activate', payload), []);
  const verifyMfa = useCallback((code, expectedUserId, signal) => coordinator.current.login('/auth/mfa/verify', { code },
    { expectedUserId, requireOk: true, requireMfaVerified: true, signal }), []);
  const logout = useCallback(async () => {
    try { return await coordinator.current.logout(); }
    finally { try { sessionStorage.removeItem('ca_profile_test_nonce'); } catch {} }
  }, []);
  const startProfileTest = useCallback(async targetUserId => {
    const { data } = await api.post('/auth/test-profile/start', { target_user_id: targetUserId });
    try { sessionStorage.setItem('ca_profile_test_nonce', data.nonce); }
    catch {
      await api.post('/auth/test-profile/stop');
      throw new Error('Tu navegador no pudo mantener el perfil de prueba.');
    }
    const target = await refresh();
    if (!target?.profile_test || target.profile_test.target_user_id !== targetUserId) {
      await api.post('/auth/test-profile/stop');
      sessionStorage.removeItem('ca_profile_test_nonce');
      await refresh();
      throw new Error('No pudimos verificar el perfil de prueba. Inténtalo de nuevo.');
    }
    return data.target;
  }, [refresh]);
  const stopProfileTest = useCallback(async () => {
    await api.post('/auth/test-profile/stop');
    sessionStorage.removeItem('ca_profile_test_nonce');
    const owner = await refresh();
    if (owner?.role !== 'super_admin') throw new Error('No pudimos restaurar tu sesión de administrador.');
    return owner;
  }, [refresh]);
  useEffect(() => {
    coordinator.current.resume(); refresh();
    return () => coordinator.current.dispose();
  }, [refresh]);
  async function retryLogout() {
    setRetrying(true);
    try { await logout(); } finally { setRetrying(false); }
  }
  return <AuthContext.Provider value={{ user: state.user, loading: state.loading, login, loginWithGoogle, registerCustomer, activateAccount, logout, refresh, verifyMfa, startProfileTest, stopProfileTest }}>
    {children}
    {state.warning && <aside role="alert" className="fixed bottom-3 inset-x-3 z-[100] rounded-xl border border-red-400 bg-neutral-950 p-4 text-white shadow-xl">
      <p>{state.warning}</p><button type="button" disabled={retrying} onClick={retryLogout} className="min-h-[44px] underline disabled:opacity-50">{retrying ? 'Cerrando…' : 'Reintentar cierre de sesión'}</button>
    </aside>}
  </AuthContext.Provider>;
}
export const useAuth = () => useContext(AuthContext);
